import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldAlert, Sun, Layers, Wrench, CheckCircle2, ArrowRight, Phone, Droplets, Wind, Hammer } from 'lucide-react';
import DynamicIsland from '../components/DynamicIsland';
import Footer from '../components/Footer';
import LeadFormPopup from '../components/LeadFormPopup';

const services = [
  {
    id: 'eternit',
    icon: ShieldAlert,
    title: 'Smaltimento Eternit e Amianto',
    subtitle: 'Bonifica certificata e rifacimento copertura',
    desc: 'La rimozione dell\'eternit e dell\'amianto richiede competenze specifiche e certificazioni obbligatorie per legge. Il nostro team e abilitato alla rimozione e allo smaltimento in sicurezza di tutte le coperture contenenti amianto.',
    image: '/lavorazioni/eternit.jpg',
    features: [
      'Analisi e campionamento dei materiali',
      'Piano di lavoro conforme D.Lgs. 81/2008',
      'Rimozione con personale abilitato e DPI specifici',
      'Smaltimento presso discariche autorizzate con tracciabilita',
      'Rifacimento completo della nuova copertura',
      'Rilascio documentazione e certificazioni di legge',
    ],
    details: 'L\'eternit, contenente fibre di amianto, rappresenta un serio rischio per la salute. La normativa italiana impone la bonifica delle coperture deteriorate. Ci occupiamo dell\'intero processo: dalla valutazione iniziale alla consegna della nuova copertura, garantendo il rispetto di tutte le normative di sicurezza e ambientali. Ogni intervento viene documentato con report fotografico e certificazione di avvenuto smaltimento.',
  },
  {
    id: 'fotovoltaico',
    icon: Sun,
    title: 'Lamiere Grecate per Fotovoltaico',
    subtitle: 'Coperture predisposte per impianti solari',
    desc: 'Installiamo lamiere grecate di ultima generazione, progettate specificamente per supportare l\'installazione di pannelli fotovoltaici. Struttura portante ottimizzata per massimizzare la resa energetica del tuo impianto.',
    image: '/lavorazioni/fotovoltaico.jpg',
    features: [
      'Lamiere grecate in acciaio zincato preverniciato',
      'Portata strutturale calcolata per pannelli FV',
      'Sistemi di fissaggio anti-sollevamento',
      'Predisposizione canali per cablaggio elettrico',
      'Compatibilita con tutti i principali sistemi FV',
      'Impermeabilizzazione garantita nei punti di fissaggio',
    ],
    details: 'Le nostre lamiere grecate sono la soluzione ideale per chi vuole installare un impianto fotovoltaico su coperture industriali, agricole o residenziali. Utilizziamo profili in acciaio di alta qualita con trattamento anticorrosione e verniciatura a forno. La struttura e calcolata per sostenere il peso dei pannelli solari e resistere alle sollecitazioni del vento, garantendo una durata superiore a 30 anni.',
  },
  {
    id: 'coibentati',
    icon: Layers,
    title: 'Pannelli Coibentati',
    subtitle: 'Isolamento termico con estetica tradizionale',
    desc: 'Pannelli sandwich coibentati disponibili sia nella versione standard che con finitura finto coppo. Garantiscono isolamento termico superiore mantenendo l\'estetica tradizionale del tetto italiano.',
    image: '/lavorazioni/coibentati.jpg',
    features: [
      'Pannelli sandwich con anima in poliuretano o lana di roccia',
      'Spessori da 30mm a 120mm per ogni esigenza',
      'Finitura esterna finto coppo in vari colori',
      'Coefficiente di trasmittanza termica eccellente',
      'Resistenza al fuoco classificata (su richiesta)',
      'Posa rapida e pulita senza opere murarie invasive',
    ],
    details: 'I pannelli coibentati rappresentano la soluzione piu efficiente per il rifacimento delle coperture. La versione con finto coppo e particolarmente apprezzata nelle zone con vincoli paesaggistici, dove e necessario mantenere l\'estetica tradizionale. L\'isolamento integrato riduce drasticamente la dispersione termica, con un risparmio energetico che puo raggiungere il 40% sui costi di riscaldamento e raffrescamento. Disponibili anche con trattamento antibatterico e antimuffa.',
  },
  {
    id: 'manutenzione',
    icon: Wrench,
    title: 'Manutenzione e Riparazione Tetti',
    subtitle: 'Interventi rapidi senza ponteggi',
    desc: 'Servizio completo di manutenzione ordinaria e straordinaria per tutti i tipi di copertura. Operando senza ponteggi, riduciamo tempi e costi di intervento fino al 70%.',
    image: '/brochure/tecnico-posa-coppi.jpg',
    features: [
      'Revisione completa del manto di copertura',
      'Fissaggio e sostituzione coppi e tegole',
      'Impermeabilizzazione comignoli e lucernari',
      'Pulizia grondaie e canali di scolo',
      'Installazione linee vita certificate',
      'Report fotografico pre e post intervento',
    ],
    details: 'La manutenzione regolare del tetto previene danni costosi e prolunga la vita della copertura. I nostri tecnici certificati operano in quota con sistemi di sicurezza avanzati, eliminando la necessita di ponteggi costosi. Ogni intervento include un\'ispezione completa della copertura con report fotografico dettagliato e raccomandazioni per eventuali interventi futuri.',
  },
];

const additionalServices = [
  { icon: Droplets, title: 'Impermeabilizzazione', desc: 'Trattamenti impermeabilizzanti con tecnologia RevoUltra per una protezione definitiva.' },
  { icon: Wind, title: 'Ventilazione sottotetto', desc: 'Installazione di sistemi di ventilazione per prevenire condensa e umidita.' },
  { icon: Hammer, title: 'Lattoneria', desc: 'Lavorazione e installazione di grondaie, scossaline, converse e colmi.' },
];

const LavorazioniPage = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <DynamicIsland onOpenForm={() => setIsFormOpen(true)} />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-slate-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,87,34,0.04),transparent_50%)]" />
        <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
          <span className="text-[#FF5722] text-sm font-semibold uppercase tracking-wider">Le nostre lavorazioni</span>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mt-4 mb-6">
              Soluzioni complete per la tua copertura
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Dallo smaltimento dell'amianto all'installazione di coperture per fotovoltaico, offriamo un servizio chiavi in mano con tecnici certificati e garanzia scritta.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Detail */}
      {services.map((service, i) => (
        <section key={service.id} id={service.id} className={`py-20 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${i % 2 !== 0 ? 'lg:grid-flow-dense' : ''}`}
            >
              {/* Image */}
              <div className={`${i % 2 !== 0 ? 'lg:col-start-2' : ''}`}>
                <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-[350px] object-cover"
                  />
                </div>
              </div>

              {/* Content */}
              <div>
                <div className="w-12 h-12 bg-[#FF5722]/10 rounded-xl flex items-center justify-center mb-5">
                  <service.icon className="w-6 h-6 text-[#FF5722]" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">{service.title}</h2>
                <p className="text-[#FF5722] text-sm font-medium mb-4">{service.subtitle}</p>
                <p className="text-slate-600 leading-relaxed mb-6">{service.details}</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                  {service.features.map((f) => (
                    <div key={f} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-700">{f}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => setIsFormOpen(true)}
                  className="inline-flex items-center gap-2 bg-[#FF5722] hover:bg-[#FF7043] text-white font-bold px-6 py-3 rounded-full transition-all duration-300 hover:shadow-[0_0_20px_rgba(255,87,34,0.3)] hover:-translate-y-0.5"
                >
                  Richiedi Preventivo Gratuito
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      ))}

      {/* Additional Services */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-10 text-center">Servizi aggiuntivi</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {additionalServices.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#FF5722]/30 hover:shadow-md transition-all duration-300"
              >
                <s.icon className="w-6 h-6 text-[#FF5722] mb-4" />
                <h3 className="text-slate-900 font-semibold mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-3xl mx-auto px-6 md:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Hai un progetto in mente?</h2>
          <p className="text-slate-300 mb-8">Contattaci per un sopralluogo gratuito e un preventivo su misura.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => setIsFormOpen(true)}
              className="inline-flex items-center gap-2 bg-[#FF5722] hover:bg-[#FF7043] text-white font-bold px-8 py-4 rounded-full transition-all"
            >
              Sopralluogo Gratuito
              <ArrowRight className="w-4 h-4" />
            </button>
            <a href="tel:800864621" className="inline-flex items-center gap-2 text-white border border-white/20 hover:border-white/40 px-8 py-4 rounded-full transition-all">
              <Phone className="w-4 h-4" />
              800 86 46 21
            </a>
          </div>
        </div>
      </section>

      <Footer />

      <LeadFormPopup isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />

      {/* WhatsApp */}
      <a
        href="https://wa.me/393791617599"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl transition-all hover:scale-110"
        data-testid="whatsapp-button"
      >
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>
    </div>
  );
};

export default LavorazioniPage;
